<template>
    <b-modal v-model="visibleModel" size="md" centered @show="onShow">
        <template #modal-header="{ close }">
            <div class="elite-header-inner">
                <div class="facility-permission-header-text">
                    <span class="elite-title-inner">시설 권한</span>
                    <span v-if="user" class="facility-permission-user-inline">
                        {{ user.userName }} <span class="facility-permission-user-id">({{ user.loginId }})</span>
                    </span>
                </div>
                <b-button variant="link" class="elite-header-close p-0 ml-auto" aria-label="닫기" @click="close()">
                    <i class="fas fa-times"></i>
                </b-button>
            </div>
        </template>

        <div v-if="user" class="permission-content">
            <div v-if="loading" class="text-center py-3"><i class="fas fa-spinner fa-spin"></i> 로딩 중...</div>
            <template v-else>
                <div class="table-container-compact">
                    <table class="table-compact">
                        <thead>
                            <tr>
                                <th class="text-center">시설 코드</th>
                                <th class="text-center">시설명</th>
                                <th class="text-center">접근 레벨</th>
                                <th class="text-center">비고</th>
                                <th class="text-center col-action"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="facilities.length === 0">
                                <td colspan="5" class="text-center empty-state">
                                    <i class="fas fa-building"></i>
                                    <p>부여된 시설 권한이 없습니다.</p>
                                </td>
                            </tr>
                            <tr v-for="f in facilities" :key="f.facilityIdx">
                                <td class="text-center">
                                    <strong class="facility-code-cell">{{ f.facilityCode }}</strong>
                                </td>
                                <td class="text-center">{{ f.facilityName || "-" }}</td>
                                <td class="text-center">{{ formatAccessLevel(f.accessLevel) }}</td>
                                <td class="text-center">{{ f.remarks || "-" }}</td>
                                <td class="text-center col-action">
                                    <div class="action-buttons">
                                        <IconButton icon="fas fa-edit" size="small" title="수정" @click="openEdit(f)" />
                                        <IconButton
                                            icon="fas fa-times"
                                            size="small"
                                            variant="delete"
                                            title="권한 회수"
                                            @click="revokePermission(f)"
                                        />
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="form-section add-section">
                    <div class="section-header">
                        <h6>시설 권한 부여</h6>
                    </div>
                    <div class="add-form-row">
                        <div class="form-group">
                            <label>시설</label>
                            <CarbonSelect
                                v-model="addForm.facilityIdx"
                                :options="facilityOptionsForSelect"
                                placeholder="선택하세요"
                                custom-class="form-control-compact"
                            />
                        </div>
                        <div class="form-group">
                            <label>접근 레벨</label>
                            <CarbonSelect
                                v-model="addForm.accessLevel"
                                :options="accessLevelOptions"
                                placeholder="선택하세요"
                                custom-class="form-control-compact"
                            />
                        </div>
                        <div class="form-group">
                            <label>비고</label>
                            <CarbonInput
                                v-model="addForm.remarks"
                                placeholder="선택"
                                custom-class="form-control-compact"
                            />
                        </div>
                        <div class="form-group form-group-btn">
                            <CarbonButton
                                variant="primary"
                                icon="fas fa-plus"
                                :loading="adding"
                                :disabled="adding || !addForm.facilityIdx || !addForm.accessLevel"
                                @click="grantPermission"
                            >
                                부여
                            </CarbonButton>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <template #modal-footer>
            <CarbonButton variant="secondary" @click="visibleModel = false">닫기</CarbonButton>
        </template>

        <!-- 수정 모달 -->
        <b-modal v-model="showEditModal" title="시설 권한 수정" size="sm" centered @ok.prevent="saveEdit">
            <template #modal-header="{ close }">
                <div class="modal-detail-header">
                    <h5 class="modal-title">시설 권한 수정</h5>
                    <button type="button" class="close" @click="close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            </template>
            <div v-if="editingRow" class="modal-form-content">
                <div class="form-group">
                    <label>시설</label>
                    <p class="detail-value-plain">{{ editingRow.facilityCode }} {{ editingRow.facilityName || "" }}</p>
                </div>
                <div class="form-group">
                    <label>접근 레벨</label>
                    <CarbonSelect
                        v-model="editForm.accessLevel"
                        :options="accessLevelOptions"
                        placeholder="선택하세요"
                        custom-class="form-control-compact"
                    />
                </div>
                <div class="form-group">
                    <label>비고</label>
                    <CarbonInput v-model="editForm.remarks" placeholder="비고" custom-class="form-control-compact" />
                </div>
            </div>
            <template #modal-footer>
                <CarbonButton variant="secondary" @click="showEditModal = false">취소</CarbonButton>
                <CarbonButton variant="primary" :loading="savingEdit" :disabled="savingEdit" @click="saveEdit"
                    >저장</CarbonButton
                >
            </template>
        </b-modal>
    </b-modal>
</template>

<script>
import carbonApi from "../../../api";
import CarbonCodes from "../../../codes";
import accessControlMixin from "../mixins/accessControlMixin";
import CarbonSelect from "../../common/CarbonSelect.vue";
import CarbonInput from "../../common/CarbonInput.vue";
import CarbonButton from "../../common/CarbonButton.vue";
import IconButton from "../../common/IconButton.vue";

export default {
    name: "UserFacilityPermissionModal",
    components: {
        CarbonSelect,
        CarbonInput,
        CarbonButton,
        IconButton,
    },
    mixins: [accessControlMixin],
    props: {
        value: { type: Boolean, default: false },
        user: { type: Object, default: null },
    },
    data() {
        return {
            facilities: [],
            allFacilities: [],
            loading: false,
            adding: false,
            savingEdit: false,
            addForm: {
                facilityIdx: null,
                accessLevel: "READ",
                remarks: "",
            },
            showEditModal: false,
            editingRow: null,
            editForm: { accessLevel: "READ", remarks: "" },
        };
    },
    computed: {
        visibleModel: {
            get() {
                return this.value;
            },
            set(v) {
                this.$emit("input", v);
            },
        },
        assignedFacilityIds() {
            return new Set(this.facilities.map((f) => f.facilityIdx).filter(Boolean));
        },
        availableFacilities() {
            return this.allFacilities.filter((f) => !this.assignedFacilityIds.has(f.facilityIdx));
        },
        facilityOptionsForSelect() {
            return this.availableFacilities.map((fac) => ({
                value: fac.facilityIdx,
                text: `${fac.facilityCode} — ${fac.facilityName || "-"}`,
            }));
        },
        accessLevelOptions() {
            return CarbonCodes.accessLevel
                ? CarbonCodes.accessLevel.getList()
                : [
                      { value: "READ", text: "읽기" },
                      { value: "WRITE", text: "쓰기" },
                      { value: "FULL", text: "전체" },
                  ];
        },
    },
    methods: {
        formatAccessLevel(level) {
            return CarbonCodes.accessLevel ? CarbonCodes.accessLevel.getLabel(level) : level || "-";
        },
        async onShow() {
            if (this.user?.loginId) {
                await this.loadPermissions();
                await this.loadFacilities();
            }
            this.addForm = { facilityIdx: null, accessLevel: "READ", remarks: "" };
        },
        async loadPermissions() {
            if (!this.user?.loginId) return;
            this.loading = true;
            try {
                const { data } = await carbonApi.accessControl.getFacilityPermissions(this.user.loginId);
                if (data?.success && data.data?.facilities) {
                    this.facilities = data.data.facilities;
                } else {
                    this.facilities = [];
                }
            } catch (err) {
                console.error("[UserFacilityPermissionModal] 시설 권한 조회 실패:", err);
                this.facilities = [];
            } finally {
                this.loading = false;
            }
        },
        async loadFacilities() {
            try {
                const res = await carbonApi.utility.getFacilities(null, { limit: 200 });
                const data = res.data?.data != null ? res.data.data : res.data;
                this.allFacilities = (data && data.facilities) || [];
            } catch (err) {
                console.error("[UserFacilityPermissionModal] 시설 목록 조회 실패:", err);
                this.allFacilities = [];
            }
        },
        async grantPermission() {
            const facilityIdx = this.addForm.facilityIdx;
            if (!this.user?.loginId || (facilityIdx !== 0 && !facilityIdx) || !this.addForm.accessLevel) return;
            this.adding = true;
            try {
                await carbonApi.accessControl.assignFacilityPermission(this.user.loginId, {
                    facilityIdx: Number(facilityIdx),
                    accessLevel: this.addForm.accessLevel,
                    remarks: this.addForm.remarks?.trim() || null,
                });
                this.toastSuccess("시설 권한이 부여되었습니다.");
                this.addForm = { facilityIdx: null, accessLevel: "READ", remarks: "" };
                await this.loadPermissions();
                this.$emit("updated");
            } catch (err) {
                this.toastError(this.getErrorMessage(err, "시설 권한 부여에 실패했습니다."));
            } finally {
                this.adding = false;
            }
        },
        openEdit(row) {
            this.editingRow = row;
            this.editForm = {
                accessLevel: row.accessLevel || "READ",
                remarks: row.remarks || "",
            };
            this.showEditModal = true;
        },
        async saveEdit() {
            if (!this.user?.loginId || !this.editingRow?.facilityIdx) return;
            this.savingEdit = true;
            try {
                await carbonApi.accessControl.updateFacilityPermission(this.user.loginId, this.editingRow.facilityIdx, {
                    accessLevel: this.editForm.accessLevel,
                    remarks: this.editForm.remarks,
                });
                this.toastSuccess("시설 권한이 수정되었습니다.");
                this.showEditModal = false;
                this.editingRow = null;
                await this.loadPermissions();
                this.$emit("updated");
            } catch (err) {
                this.toastError(this.getErrorMessage(err, "시설 권한 수정에 실패했습니다."));
            } finally {
                this.savingEdit = false;
            }
        },
        async revokePermission(row) {
            if (!this.user?.loginId || row.facilityIdx == null) return;
            const name = row.facilityName || row.facilityCode || "해당 시설";
            const ok = await this.$bvModal.msgBoxConfirm(`"${name}" 시설 권한을 회수하시겠습니까?`, {
                title: "시설 권한 회수",
                okTitle: "회수",
                cancelTitle: "취소",
                okVariant: "danger",
            });
            if (!ok) return;
            try {
                await carbonApi.accessControl.revokeFacilityPermission(this.user.loginId, row.facilityIdx);
                this.toastSuccess("시설 권한이 회수되었습니다.");
                await this.loadPermissions();
                this.$emit("updated");
            } catch (err) {
                this.toastError(this.getErrorMessage(err, "시설 권한 회수에 실패했습니다."));
            }
        },
    },
};
</script>

<style scoped>
.elite-header-inner {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
}

.elite-title-inner {
    font-size: 15px;
    font-weight: 700;
    color: #334155;
}

.facility-permission-header-text {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex: 1 1 auto;
}

.facility-permission-user-inline {
    font-size: 12px;
    color: #64748b;
}

.facility-permission-user-id {
    color: #94a3b8;
}

.elite-header-close {
    color: #9ca3af;
}

.elite-header-close:hover {
    color: #4b5563;
}
.permission-content {
    min-height: 120px;
}

/* 테이블: FacilitySettingsTab과 동일한 table-compact 스타일 */

/* 폼 섹션: FacilitySettingsTab과 동일 */
.form-section {
    margin-bottom: 16px;
}
.add-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
}
.section-header {
    margin-bottom: 8px;
}
.section-header h6 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
}
.add-form-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-end;
    gap: 12px 16px;
}
.add-form-row .form-group {
    flex: 1;
    min-width: 0;
}
.add-form-row .form-group-btn {
    flex: 0 0 auto;
}
.form-group {
    margin-bottom: 12px;
}
.form-group-btn {
    margin-bottom: 12px;
}
.form-group label {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    font-weight: 500;
}
.form-control-compact {
    width: 100%;
    min-width: 0;
}
.modal-form-content .form-group {
    min-width: 0;
}
.detail-value-plain {
    margin: 0;
    padding: 6px 0;
    font-size: 13px;
    color: #334155;
}
</style>
